import { DateTime } from "luxon";

import {
  CommodityCurrentToMonth,
  CommodityCurrentToPeriod,
  CommodityCurrentToQuarter,
  CommodityRatesToCommodity,
} from "@/generated/api/prices";
import { CommodityOutlookQueryResult } from "@/routes/stock-exchange/stock-exchange.api";
import { Range } from "@/routes/stock-exchange/stock-exchange.utils";

type MaskableCommodity = Extract<
  CommodityRatesToCommodity,
  "WHEAT" | "RAPESEED"
>;

type DummyRatesPeriod = {
  current: string;
  trend: number;
  rates: { datetime: string; rate: number }[];
};

type DummyPeriodConfig = {
  current: string;
  currentValue: number;
  trend: number;
  volatility: number;
  seed: number;
};

const isMaskableCommodity = (
  commodity: CommodityRatesToCommodity,
): commodity is MaskableCommodity =>
  commodity === "WHEAT" || commodity === "RAPESEED";

const DUMMY_CONFIG: Record<MaskableCommodity, DummyPeriodConfig[]> = {
  RAPESEED: [
    {
      current: "480,00 €",
      currentValue: 480,
      seed: 1,
      trend: 6,
      volatility: 6,
    },
    {
      current: "490,00 €",
      currentValue: 490,
      seed: 2,
      trend: -3,
      volatility: 6,
    },
  ],
  WHEAT: [
    {
      current: "210,00 €",
      currentValue: 210,
      seed: 3,
      trend: 3.5,
      volatility: 4,
    },
    {
      current: "215,00 €",
      currentValue: 215,
      seed: 4,
      trend: -1.2,
      volatility: 4,
    },
  ],
};

// Deterministic PRNG (mulberry32) so the noisy series is stable across renders.
const createSeededRandom = (seed: number) => {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Point counts are capped well below the real trading-day count per range —
// plotting hundreds of points in the chart's width just renders as a smear.
const RANGE_POINTS: Record<Range, number> = {
  all: 90,
  last_month: 21,
  last_week: 5,
  last_year: 60,
  today: 5,
};

const getTradingDays = (count: number): DateTime[] => {
  const days: DateTime[] = [];
  let cursor = DateTime.now().startOf("day");

  while (days.length < count) {
    if (cursor.weekday <= 5) {
      days.unshift(cursor);
    }
    cursor = cursor.minus({ days: 1 });
  }

  return days;
};

const generateRates = (config: DummyPeriodConfig, points: number) => {
  const random = createSeededRandom(config.seed);
  const dates = getTradingDays(points);
  let noise = 0;

  return dates.map((date, index) => {
    const trendComponent =
      config.currentValue -
      config.trend +
      (config.trend * index) / Math.max(points - 1, 1);
    const isLast = index === points - 1;
    noise = noise * 0.7 + (random() - 0.5) * config.volatility;
    const rate = trendComponent + (isLast ? 0 : noise);

    return {
      datetime: `${date.day}. ${date.month}.`,
      rate: Math.round(rate * 100) / 100,
    };
  });
};

const getDummyRates = (
  commodity: MaskableCommodity,
  range: Range,
): DummyRatesPeriod[] =>
  DUMMY_CONFIG[commodity].map((config) => ({
    current: config.current,
    rates: generateRates(config, RANGE_POINTS[range]),
    trend: config.trend,
  }));

const DUMMY_OUTLOOK: Record<MaskableCommodity, CommodityOutlookQueryResult> = {
  RAPESEED: [
    {
      month: CommodityCurrentToMonth.JANUARY,
      quarter: CommodityCurrentToQuarter.Q1,
      rate: 470,
      trend: 4,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.APRIL,
      period: CommodityCurrentToPeriod.CURRENT,
      quarter: CommodityCurrentToQuarter.Q2,
      rate: 475,
      trend: -2,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.JULY,
      quarter: CommodityCurrentToQuarter.Q3,
      rate: 480,
      trend: 1.5,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.OCTOBER,
      quarter: CommodityCurrentToQuarter.Q4,
      rate: 485,
      trend: -0.8,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.JANUARY,
      period: CommodityCurrentToPeriod.NEXT,
      quarter: CommodityCurrentToQuarter.Q1,
      rate: 490,
      trend: 2,
      year: 2027,
    },
    {
      month: CommodityCurrentToMonth.APRIL,
      quarter: CommodityCurrentToQuarter.Q2,
      rate: 492,
      trend: 0.5,
      year: 2027,
    },
  ],
  WHEAT: [
    {
      month: CommodityCurrentToMonth.JANUARY,
      quarter: CommodityCurrentToQuarter.Q1,
      rate: 205,
      trend: 2.5,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.APRIL,
      period: CommodityCurrentToPeriod.CURRENT,
      quarter: CommodityCurrentToQuarter.Q2,
      rate: 208,
      trend: -1,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.JULY,
      quarter: CommodityCurrentToQuarter.Q3,
      rate: 210,
      trend: 1,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.OCTOBER,
      quarter: CommodityCurrentToQuarter.Q4,
      rate: 212,
      trend: -0.5,
      year: 2026,
    },
    {
      month: CommodityCurrentToMonth.JANUARY,
      period: CommodityCurrentToPeriod.NEXT,
      quarter: CommodityCurrentToQuarter.Q1,
      rate: 215,
      trend: 1.2,
      year: 2027,
    },
    {
      month: CommodityCurrentToMonth.APRIL,
      quarter: CommodityCurrentToQuarter.Q2,
      rate: 217,
      trend: 0.3,
      year: 2027,
    },
  ],
};

export { DUMMY_OUTLOOK, getDummyRates, isMaskableCommodity };
export type { DummyRatesPeriod, MaskableCommodity };
